import { useState } from 'react'
import {
  BadgeStatus,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  Input,
  PageHeader,
  Tabs,
  useToast,
  type ItemTab,
  type KolomTabel,
} from '@/components'
import { api, pesanKesalahan } from '@/lib/api'
import { formatTanggal } from '@/lib/format'
import type { PeriodeAkuntansi, TahunBuku } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { PanelCrud } from './PanelCrud'

const TAB: ItemTab[] = [
  { id: 'unit', label: 'Unit', izin: ['unit:read'] },
  { id: 'akun', label: 'Akun / COA', izin: ['account:read'] },
  { id: 'dana', label: 'Sumber Dana', izin: ['fund:manage', 'setting:read'] },
  { id: 'pusat-biaya', label: 'Pusat Biaya', izin: ['costcenter:manage', 'setting:read'] },
  { id: 'jenis-tagihan', label: 'Jenis Tagihan', izin: ['feetype:read'] },
  { id: 'kelas', label: 'Kelas', izin: ['student:read', 'academicyear:manage'] },
  { id: 'santri', label: 'Santri/Siswa', izin: ['student:read'] },
  { id: 'pemasok', label: 'Pemasok', izin: ['setting:read', 'account:read'] },
  { id: 'tahun-buku', label: 'Tahun Buku & Periode', izin: ['fiscalyear:manage', 'period:close', 'period:reopen'] },
  { id: 'pengaturan', label: 'Pengaturan Akuntansi', izin: ['setting:read'] },
]

export default function MasterData() {
  const [tab, setTab] = useState('unit')
  const opsiUnit = useOpsiUnit()

  return (
    <div>
      <PageHeader
        judul="Master Data"
        deskripsi="Data referensi keuangan dan akademik: unit, bagan akun, sumber dana, pusat biaya, jenis tagihan, kelas, santri, pemasok, tahun buku, dan pengaturan."
      />

      <Tabs tab={TAB} aktif={tab} onUbah={setTab} />

      {tab === 'unit' ? (
        <PanelCrud
          path="/units"
          judul="Unit Organisasi"
          deskripsi="Yayasan, pondok pesantren, sekolah, dan unit lain dalam satu entitas."
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama' },
            { kunci: 'unitType', judul: 'Jenis', tipe: 'status' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode Unit', wajib: true },
            { kunci: 'name', label: 'Nama Unit', wajib: true },
            {
              kunci: 'unitType',
              label: 'Jenis Unit',
              tipe: 'pilih',
              opsi: ['YAYASAN', 'PONDOK', 'SEKOLAH', 'UNIT_LAIN'].map((v) => ({ value: v, label: v })),
            },
            { kunci: 'address', label: 'Alamat', tipe: 'area', lebarPenuh: true },
            { kunci: 'phone', label: 'Telepon' },
            { kunci: 'isConsolidated', label: 'Ikut dikonsolidasikan', tipe: 'centang', nilaiBawaan: true },
          ]}
        />
      ) : null}

      {tab === 'akun' ? (
        <PanelCrud
          path="/accounts"
          judul="Bagan Akun (COA)"
          deskripsi="Akun induk tidak dapat diposting — hanya akun turunan (isPostable) yang boleh menerima jurnal."
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama Akun' },
            { kunci: 'accountType', judul: 'Tipe' },
            { kunci: 'normalBalance', judul: 'Saldo Normal' },
            { kunci: 'isPostable', judul: 'Dapat Diposting' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode Akun', wajib: true },
            { kunci: 'name', label: 'Nama Akun', wajib: true },
            {
              kunci: 'accountType',
              label: 'Tipe Akun',
              tipe: 'pilih',
              wajib: true,
              opsi: ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE', 'CONTRA_ASSET', 'CONTRA_REVENUE'].map((v) => ({
                value: v,
                label: v,
              })),
            },
            {
              kunci: 'normalBalance',
              label: 'Saldo Normal',
              tipe: 'pilih',
              wajib: true,
              opsi: [
                { value: 'DEBIT', label: 'Debit' },
                { value: 'CREDIT', label: 'Kredit' },
              ],
            },
            { kunci: 'parentAccountId', label: 'ID Akun Induk', bantuan: 'Kosongkan bila akun tingkat atas.' },
            { kunci: 'isPostable', label: 'Dapat diposting', tipe: 'centang', nilaiBawaan: true },
            { kunci: 'requireFund', label: 'Wajib sumber dana', tipe: 'centang' },
            { kunci: 'requireCostCenter', label: 'Wajib pusat biaya', tipe: 'centang' },
            { kunci: 'description', label: 'Keterangan', tipe: 'area', lebarPenuh: true },
          ]}
        />
      ) : null}

      {tab === 'dana' ? (
        <PanelCrud
          path="/funds"
          judul="Sumber Dana"
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama' },
            { kunci: 'restrictionType', judul: 'Sifat' },
            { kunci: 'donorName', judul: 'Donor' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode', wajib: true },
            { kunci: 'name', label: 'Nama Sumber Dana', wajib: true },
            {
              kunci: 'restrictionType',
              label: 'Sifat Keterikatan',
              tipe: 'pilih',
              opsi: ['UNRESTRICTED', 'TEMPORARILY_RESTRICTED', 'PERMANENTLY_RESTRICTED'].map((v) => ({
                value: v,
                label: v,
              })),
            },
            { kunci: 'donorName', label: 'Nama Donor' },
            { kunci: 'startDate', label: 'Tanggal Mulai', tipe: 'tanggal' },
            { kunci: 'endDate', label: 'Tanggal Selesai', tipe: 'tanggal' },
            { kunci: 'restrictionDescription', label: 'Keterangan Pembatasan', tipe: 'area', lebarPenuh: true },
          ]}
        />
      ) : null}

      {tab === 'pusat-biaya' ? (
        <PanelCrud
          path="/cost-centers"
          judul="Pusat Biaya / Program"
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama Program' },
            { kunci: 'programType', judul: 'Jenis Program' },
            { kunci: 'budgetAmount', judul: 'Anggaran', tipe: 'rupiah', rata: 'kanan' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode', wajib: true },
            { kunci: 'name', label: 'Nama Program', wajib: true },
            { kunci: 'unitId', label: 'Unit', tipe: 'pilih', opsi: opsiUnit },
            { kunci: 'programType', label: 'Jenis Program' },
            { kunci: 'budgetAmount', label: 'Anggaran (Rp)', tipe: 'angka' },
            { kunci: 'startDate', label: 'Tanggal Mulai', tipe: 'tanggal' },
            { kunci: 'endDate', label: 'Tanggal Selesai', tipe: 'tanggal' },
          ]}
        />
      ) : null}

      {tab === 'jenis-tagihan' ? (
        <PanelCrud
          path="/fee-types"
          judul="Jenis Tagihan"
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama' },
            { kunci: 'category', judul: 'Kategori' },
            { kunci: 'billingPeriodType', judul: 'Periode' },
            { kunci: 'defaultAmount', judul: 'Nominal', tipe: 'rupiah', rata: 'kanan' },
            { kunci: 'recognitionPolicy', judul: 'Pengakuan' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode', wajib: true },
            { kunci: 'name', label: 'Nama Jenis Tagihan', wajib: true },
            { kunci: 'unitId', label: 'Unit', tipe: 'pilih', opsi: opsiUnit },
            { kunci: 'category', label: 'Kategori' },
            {
              kunci: 'billingPeriodType',
              label: 'Periode Penagihan',
              tipe: 'pilih',
              opsi: ['MONTHLY', 'YEARLY', 'ONE_TIME', 'SEMESTER'].map((v) => ({ value: v, label: v })),
            },
            { kunci: 'defaultAmount', label: 'Nominal Default (Rp)', tipe: 'angka' },
            {
              kunci: 'recognitionPolicy',
              label: 'Kebijakan Pengakuan Pendapatan',
              tipe: 'pilih',
              opsi: [
                { value: 'ON_INVOICE', label: 'Saat Tagihan Terbit' },
                { value: 'ON_PAYMENT', label: 'Saat Pembayaran Diterima' },
                { value: 'DEFERRED', label: 'Ditangguhkan' },
              ],
            },
            { kunci: 'allowsPartialPayment', label: 'Izinkan pembayaran sebagian', tipe: 'centang', nilaiBawaan: true },
            { kunci: 'lateFeeEnabled', label: 'Aktifkan denda keterlambatan', tipe: 'centang' },
            { kunci: 'description', label: 'Keterangan', tipe: 'area', lebarPenuh: true },
          ]}
        />
      ) : null}

      {tab === 'kelas' ? (
        <PanelCrud
          path="/classes"
          judul="Kelas"
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama Kelas' },
            { kunci: 'level', judul: 'Tingkat' },
            { kunci: 'capacity', judul: 'Kapasitas', tipe: 'angka', rata: 'kanan' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode Kelas', wajib: true },
            { kunci: 'name', label: 'Nama Kelas', wajib: true },
            { kunci: 'unitId', label: 'Unit', tipe: 'pilih', opsi: opsiUnit, wajib: true },
            { kunci: 'academicYearId', label: 'ID Tahun Ajaran', bantuan: 'Ambil dari data tahun ajaran.' },
            { kunci: 'level', label: 'Tingkat' },
            { kunci: 'capacity', label: 'Kapasitas', tipe: 'angka' },
          ]}
        />
      ) : null}

      {tab === 'santri' ? (
        <PanelCrud
          path="/students"
          judul="Santri / Siswa"
          deskripsi="Satu orang (persons) hanya boleh ada sekali meski memiliki banyak peran. Identitas diambil dari data Person."
          kolom={[
            { kunci: 'studentNumber', judul: 'NIS/NISN' },
            { kunci: 'person', judul: 'Nama' },
            { kunci: 'studentType', judul: 'Jenis' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
            { kunci: 'residenceStatus', judul: 'Mukim' },
          ]}
          field={[
            { kunci: 'personId', label: 'ID Person', wajib: true, bantuan: 'Daftarkan orang di menu Person lebih dahulu.' },
            { kunci: 'unitId', label: 'Unit', tipe: 'pilih', opsi: opsiUnit, wajib: true },
            { kunci: 'studentNumber', label: 'Nomor Induk Santri', wajib: true },
            {
              kunci: 'studentType',
              label: 'Jenis',
              tipe: 'pilih',
              wajib: true,
              opsi: [
                { value: 'SANTRI', label: 'Santri' },
                { value: 'SISWA', label: 'Siswa' },
              ],
            },
            { kunci: 'nisn', label: 'NISN' },
            {
              kunci: 'residenceStatus',
              label: 'Status Mukim',
              tipe: 'pilih',
              opsi: [
                { value: 'MUKIM', label: 'Mukim (Boarding)' },
                { value: 'NONMUKIM', label: 'Non Mukim' },
              ],
            },
          ]}
        />
      ) : null}

      {tab === 'pemasok' ? (
        <PanelCrud
          path="/suppliers"
          judul="Pemasok"
          kolom={[
            { kunci: 'code', judul: 'Kode' },
            { kunci: 'name', judul: 'Nama Pemasok' },
            { kunci: 'contactPerson', judul: 'Kontak' },
            { kunci: 'phone', judul: 'Telepon' },
            { kunci: 'category', judul: 'Kategori' },
            { kunci: 'status', judul: 'Status', tipe: 'status' },
          ]}
          field={[
            { kunci: 'code', label: 'Kode', wajib: true },
            { kunci: 'name', label: 'Nama Pemasok', wajib: true },
            { kunci: 'unitId', label: 'Unit', tipe: 'pilih', opsi: opsiUnit },
            { kunci: 'contactPerson', label: 'Nama Kontak' },
            { kunci: 'phone', label: 'Telepon' },
            { kunci: 'email', label: 'Email' },
            { kunci: 'npwp', label: 'NPWP' },
            { kunci: 'paymentTermDays', label: 'Termin Pembayaran (hari)', tipe: 'angka' },
            { kunci: 'address', label: 'Alamat', tipe: 'area', lebarPenuh: true },
          ]}
        />
      ) : null}

      {tab === 'tahun-buku' ? <TabTahunBuku /> : null}
      {tab === 'pengaturan' ? <TabPengaturan /> : null}
    </div>
  )
}

function TabTahunBuku() {
  const [periodeTahun, setPeriodeTahun] = useState<TahunBuku | null>(null)
  const aksi = useAksi()
  const [konfirmasi, setKonfirmasi] = useState<{ id: string; tindakan: 'close' | 'reopen' } | null>(null)

  const periode = usePaged<PeriodeAkuntansi>(periodeTahun ? '/periods' : '/periods', {
    query: { fiscalYearId: periodeTahun?.id },
    perHalaman: 24,
  })

  const kolom: KolomTabel<PeriodeAkuntansi>[] = [
    { kunci: 'periodNumber', judul: 'Periode', render: (b) => `Ke-${b.periodNumber}` },
    { kunci: 'startDate', judul: 'Mulai', render: (b) => formatTanggal(b.startDate) },
    { kunci: 'endDate', judul: 'Selesai', render: (b) => formatTanggal(b.endDate) },
    { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
    {
      kunci: 'aksi',
      judul: 'Aksi',
      sortable: false,
      render: (b) => (
        <div className="flex flex-wrap gap-1.5">
          <Button ukuran="kecil" varian="bahaya" onClick={() => setKonfirmasi({ id: b.id, tindakan: 'close' })}>
            Tutup
          </Button>
          <Button ukuran="kecil" varian="garis" onClick={() => setKonfirmasi({ id: b.id, tindakan: 'reopen' })}>
            Buka Kembali
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <PanelCrud
        path="/fiscal-years"
        judul="Tahun Buku"
        deskripsi="Pilih tahun buku untuk melihat daftar periodenya di bawah."
        kolom={[
          { kunci: 'code', judul: 'Kode' },
          { kunci: 'name', judul: 'Nama' },
          { kunci: 'startDate', judul: 'Mulai', tipe: 'tanggal' },
          { kunci: 'endDate', judul: 'Selesai', tipe: 'tanggal' },
          { kunci: 'status', judul: 'Status', tipe: 'status' },
        ]}
        field={[
          { kunci: 'code', label: 'Kode Tahun Buku', wajib: true },
          { kunci: 'name', label: 'Nama Tahun Buku', wajib: true },
          { kunci: 'startDate', label: 'Tanggal Mulai', tipe: 'tanggal', wajib: true },
          { kunci: 'endDate', label: 'Tanggal Selesai', tipe: 'tanggal', wajib: true },
        ]}
        bisaUbah={false}
      />

      <Card padat className="overflow-hidden">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-netral-200 px-4 py-3">
          <CardHeader
            judul="Periode Akuntansi"
            deskripsi="Periode tertutup tidak menerima posting baru. Pembukaan kembali memerlukan izin khusus."
          />
          <SelectTahun nilai={periodeTahun} onUbah={setPeriodeTahun} />
        </div>
        <DataTable
          kolom={kolom}
          data={periode.items}
          kunciBaris={(b) => b.id}
          memuat={periode.memuat}
          galat={periode.galat}
          kodeGalat={periode.kodeGalat}
          onCoba={periode.muat}
          pesanKosong="Tidak ada periode akuntansi."
          meta={periode.meta}
          onHalaman={periode.setHalaman}
        />
      </Card>

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={konfirmasi?.tindakan === 'close' ? 'Tutup Periode' : 'Buka Kembali Periode'}
        pesan={
          konfirmasi?.tindakan === 'close'
            ? 'Setelah ditutup, tidak ada jurnal baru yang dapat diposting ke periode ini.'
            : 'Pembukaan kembali periode memerlukan alasan dan tercatat pada audit log.'
        }
        labelKonfirmasi={konfirmasi?.tindakan === 'close' ? 'Tutup Periode' : 'Buka Kembali'}
        berbahaya={konfirmasi?.tindakan === 'close'}
        labelAlasan={konfirmasi?.tindakan === 'reopen' ? 'Alasan pembukaan kembali' : undefined}
        minimalAlasan={10}
        memuat={aksi.memuat}
        onKonfirmasi={async (alasan) => {
          if (!konfirmasi) return
          const hasil = await aksi.jalankan(
            () =>
              api.post(`/periods/${konfirmasi.id}/${konfirmasi.tindakan}`, {
                reason: alasan || undefined,
              }),
            { sukses: 'Periode berhasil diproses.' },
          )
          if (hasil !== null) {
            setKonfirmasi(null)
            periode.muat()
          }
        }}
      />
    </div>
  )
}

function SelectTahun({
  nilai,
  onUbah,
}: {
  nilai: TahunBuku | null
  onUbah: (t: TahunBuku | null) => void
}) {
  const tahun = useApi<unknown>('/fiscal-years', { perPage: 50 })
  const daftar = Array.isArray(tahun.data)
    ? (tahun.data as TahunBuku[])
    : ((tahun.data as { items?: TahunBuku[] } | null)?.items ?? [])

  return (
    <div className="min-w-56">
      <label className="block text-sm font-medium text-netral-700" htmlFor="pilih-tahun">
        Tahun Buku
      </label>
      <select
        id="pilih-tahun"
        className="mt-1 w-full rounded-lg border border-netral-300 bg-white px-3 py-2 text-sm"
        value={nilai?.id ?? ''}
        onChange={(e) => onUbah(daftar.find((t) => t.id === e.target.value) ?? null)}
      >
        <option value="">Semua periode</option>
        {daftar.map((t) => (
          <option key={t.id} value={t.id}>
            {t.code} — {t.name}
          </option>
        ))}
      </select>
    </div>
  )
}

function TabPengaturan() {
  const toast = useToast()
  const pengaturan = usePaged<Record<string, unknown>>('/settings', { perHalaman: 100 })
  const [mengubah, setMengubah] = useState<Record<string, string>>({})
  const [menyimpan, setMenyimpan] = useState<string | null>(null)

  const simpan = async (kunci: string) => {
    const nilai = mengubah[kunci]
    if (nilai === undefined) return
    setMenyimpan(kunci)
    try {
      await api.put(`/settings/${encodeURIComponent(kunci)}`, { settingValue: nilai })
      toast.sukses(`Pengaturan ${kunci} disimpan.`)
      setMengubah((m) => {
        const salinan = { ...m }
        delete salinan[kunci]
        return salinan
      })
      pengaturan.muat()
    } catch (e) {
      toast.gagal(pesanKesalahan(e))
    } finally {
      setMenyimpan(null)
    }
  }

  return (
    <Card padat className="overflow-hidden">
      <div className="border-b border-netral-200 px-4 py-3">
        <CardHeader
          judul="Pengaturan Akuntansi"
          deskripsi="Nilai disimpan per kunci. Format tanggal YYYY-MM-DD, angka tanpa pemisah ribuan."
        />
      </div>

      {pengaturan.galat ? (
        <p className="px-4 py-3 text-sm text-red-700">Gagal memuat pengaturan: {pengaturan.galat}</p>
      ) : pengaturan.memuat ? (
        <p className="px-4 py-3 text-sm text-netral-500">Memuat pengaturan…</p>
      ) : pengaturan.items.length === 0 ? (
        <p className="px-4 py-3 text-sm text-netral-500">Belum ada pengaturan tersimpan.</p>
      ) : (
        <div className="divide-y divide-netral-100">
          {pengaturan.items.map((p, i) => {
            const kunci = String(p.settingKey ?? p.key ?? `pengaturan-${i}`)
            const nilai = String(p.settingValue ?? p.value ?? '')
            const ubah = mengubah[kunci]
            return (
              <div key={kunci} className="grid grid-cols-1 gap-2 px-4 py-3 sm:grid-cols-[1fr_1fr_auto]">
                <div>
                  <p className="text-sm font-medium text-netral-800">{kunci}</p>
                  <p className="text-xs text-netral-500">
                    {String(p.category ?? 'umum')} · {String(p.dataType ?? 'STRING')}
                    {p.description ? ` · ${String(p.description)}` : ''}
                  </p>
                </div>
                <Input
                  value={ubah ?? nilai}
                  onChange={(e) => setMengubah((m) => ({ ...m, [kunci]: e.target.value }))}
                />
                <Button
                  ukuran="kecil"
                  varian="garis"
                  disabled={ubah === undefined}
                  memuat={menyimpan === kunci}
                  onClick={() => void simpan(kunci)}
                >
                  Simpan
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}
